import { first } from 'lodash';
import { useEffect, useMemo } from 'react';

import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useRecordNotFoundHandler } from '@/hooks/useRecordNotFoundHandler';
import { EntityDocumentViewer_FilePageFragment } from '@/modules/documents/components/HouseholdDocumentsViewer/graphql/EntityDocumentViewer.generated';
import { useDocumentDetailsQuery } from '@/modules/documents/graphql/DocumentDetails.generated';
import { SelectableDocument } from '@/modules/documents/ManageDocumentAmendments/types';
import { useCreateMemoModalDocumentsQuery } from '@/modules/memos/graphql/CreateMemoModalDocuments.generated';
import {
  MemoDetails_MemoFragment,
  useMemoDetailsQuery,
} from '@/modules/memos/graphql/MemoDetails.generated';
import type { PDFSource } from '@/modules/pdf/PDFViewer';
import { MemoPromptMemoPromptType } from '@/types/schema';
import { getNodes } from '@/utils/graphqlUtils';

interface UseEditMemoTrowserDataInput {
  householdId: string;
  memoId: string;
  selectedDocumentId: string | null;
  onNotFound: () => void;
}

interface UseEditMemoTrowserDataResult {
  memo: MemoDetails_MemoFragment | null;
  activeDocumentId: string | null;
  associatedDocuments: SelectableDocument[];
  source: PDFSource | null;
  documentFilePages: EntityDocumentViewer_FilePageFragment[];
  loading: boolean;
}

export function useEditMemoTrowserData({
  householdId,
  memoId,
  selectedDocumentId,
  onNotFound,
}: UseEditMemoTrowserDataInput): UseEditMemoTrowserDataResult {
  const { createErrorFeedback } = useFeedback();
  const { recoverMissingRecord } = useRecordNotFoundHandler({
    message: 'This memo is no longer available.',
    onNotFound,
  });

  const { data: memoData, loading: memoLoading } = useMemoDetailsQuery({
    variables: { memoId },
    onError: createErrorFeedback(
      `We couldn't load this memo. Please refresh and try again.`
    ),
  });

  const memo = first(getNodes(memoData?.memos)) ?? null;
  useEffect(() => {
    if (memoData && !memo) {
      recoverMissingRecord();
    }
  }, [memo, memoData, recoverMissingRecord]);

  const { data: documentsData, loading: documentsLoading } =
    useCreateMemoModalDocumentsQuery({
      fetchPolicy: 'cache-and-network',
      variables: {
        where: {
          hasHouseholdWith: [{ id: householdId }],
        },
      },
      onError: createErrorFeedback(
        `We couldn't load household documents. Please refresh and try again.`
      ),
    });

  const associatedDocuments = useMemo(() => {
    const allHouseholdDocuments = getNodes(documentsData?.documents);
    const isTrustComparisonMemo =
      memo?.memoPrompt?.memoPromptType ===
      MemoPromptMemoPromptType.TrustComparison;

    if (!isTrustComparisonMemo) {
      return allHouseholdDocuments.map((document) => ({
        id: document.id,
        name: document.name,
        type: document.type,
      }));
    }

    const memoDocumentIds = new Set(
      getNodes(memo?.documents).map((document) => document.id)
    );

    return allHouseholdDocuments
      .filter((document) => memoDocumentIds.has(document.id))
      .map((document) => ({
        id: document.id,
        name: document.name,
        type: document.type,
      }));
  }, [
    documentsData?.documents,
    memo?.documents,
    memo?.memoPrompt?.memoPromptType,
  ]);

  const activeDocumentId = useMemo(() => {
    if (!associatedDocuments.length) {
      return null;
    }

    if (
      selectedDocumentId &&
      associatedDocuments.some((document) => {
        return document.id === selectedDocumentId;
      })
    ) {
      return selectedDocumentId;
    }

    return associatedDocuments[0]?.id ?? null;
  }, [associatedDocuments, selectedDocumentId]);

  const { data: activeDocumentData, loading: activeDocumentLoading } =
    useDocumentDetailsQuery({
      skip: !activeDocumentId,
      variables: {
        where: {
          id: activeDocumentId ?? '',
        },
      },
      onError: createErrorFeedback(
        `We couldn't load the selected document. Please refresh and try again.`
      ),
    });

  const activeDocument = first(getNodes(activeDocumentData?.documents)) ?? null;
  const activeFile = activeDocument?.pdfFile ?? activeDocument?.file ?? null;
  const source: PDFSource | null = activeFile?.id
    ? { kind: 'file', fileId: activeFile.id }
    : null;

  return {
    memo,
    activeDocumentId,
    associatedDocuments,
    source,
    documentFilePages: activeFile?.pages ?? [],
    loading: memoLoading || documentsLoading || activeDocumentLoading,
  };
}
